import { useState } from 'react';
import BulletListEditor from './BulletListEditor';
import TranslationInput from './TranslationInput';
import IconButton from '../IconButton/IconButton';
import Button from '../Button/Button';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { emptyTranslation } from '../../utils/translationUtils';
import './SectionEditor.css';

const SectionEditor = ({ sections, onChange }) => {
  const [collapsed, setCollapsed] = useState({});
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);

  const toggleCollapse = (idx) => {
    setCollapsed(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleSectionChange = (idx, field, value) => {
    onChange(sections.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const handleBulletsChange = (idx, bullets) => {
    onChange(sections.map((s, i) => i === idx ? { ...s, bullets } : s));
  };

  const handleTypeChange = (idx, type) => {
    onChange(sections.map((s, i) => i === idx ? { ...s, type, bullets: [] } : s));
  };

  const handleDisplayChange = (idx, display) => {
    onChange(sections.map((s, i) => i === idx ? { ...s, display } : s));
  };

  const addSection = () => {
    onChange([...sections, { name: emptyTranslation(), type: 'string', display: 'list', bullets: [] }]);
  };

  const removeSection = (idx) => {
    onChange(sections.filter((_, i) => i !== idx));
    setCollapsed(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = parseInt(k);
        if (ki < idx) next[ki] = v;
        else if (ki > idx) next[ki - 1] = v;
      });
      return next;
    });
    setConfirmDeleteIdx(null);
  };

  return (
    <div className="section-editor">
      {sections.map((section, idx) => {
        const isCollapsed = !!collapsed[idx];
        const sectionTitle = section.name.en.trim() || `Section ${idx + 1}`;

        return (
          <div className="section-panel" key={idx}>
            <div className="section-header">
              <IconButton
                icon="delete"
                variant="danger"
                onClick={(e) => { e.stopPropagation(); setConfirmDeleteIdx(idx); }}
                aria-label="Remove section"
              />
              <span className="section-title" onClick={() => toggleCollapse(idx)}>{sectionTitle}</span>
              <span
                className={`material-symbols-outlined section-chevron${isCollapsed ? ' collapsed' : ''}`}
                onClick={() => toggleCollapse(idx)}
              >
                expand_less
              </span>
            </div>

            {!isCollapsed && (
              <div className="section-body">
                <TranslationInput
                  label="Section Name"
                  value={section.name}
                  onChange={(lang, val) => handleSectionChange(idx, 'name', { ...section.name, [lang]: val })}
                />
                <div className="section-type-toggle">
                  <button
                    type="button"
                    className={section.type === 'string' ? 'active' : ''}
                    onClick={() => handleTypeChange(idx, 'string')}
                  >
                    Bullets
                  </button>
                  <button
                    type="button"
                    className={section.type === 'keyvalue' ? 'active' : ''}
                    onClick={() => handleTypeChange(idx, 'keyvalue')}
                  >
                    Key:Value
                  </button>
                </div>
                {section.type === 'string' && (
                  <div className="section-display-toggle">
                    <span className="section-display-label">Display</span>
                    <div className="section-type-toggle">
                      {['list', 'compact'].map(style => (
                        <button
                          key={style}
                          type="button"
                          className={(section.display || 'list') === style ? 'active' : ''}
                          onClick={() => handleDisplayChange(idx, style)}
                        >
                          {style.charAt(0).toUpperCase() + style.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <BulletListEditor
                  type={section.type}
                  bullets={section.bullets}
                  onChange={bullets => handleBulletsChange(idx, bullets)}
                />
              </div>
            )}
          </div>
        );
      })}

      <Button variant="secondary" fullWidth size="sm" onClick={addSection}>
        <span className="material-symbols-outlined">add</span>
        Add Section
      </Button>

      {confirmDeleteIdx !== null && (
        <ConfirmModal
          title="Remove Section"
          message={`Remove "${sections[confirmDeleteIdx]?.name?.en?.trim() || `Section ${confirmDeleteIdx + 1}`}"? This cannot be undone.`}
          confirmLabel="Remove"
          onConfirm={() => removeSection(confirmDeleteIdx)}
          onCancel={() => setConfirmDeleteIdx(null)}
        />
      )}
    </div>
  );
};

export default SectionEditor;
